import { AlertV2, SimpleAlert } from '@cimplico/components';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useWorkpaperContext, WorkpaperProvider } from './providers/WorkpaperProvider';
import { useWorkpaper } from './queries/use-workpaper';
import { useEntity } from './queries/use-entity';
import { useRefreshFYIDocuments } from './queries/use-refresh-fyi-documents';
import { useRefreshXeroEntity } from './queries/use-refresh-xero-entity';
import { del } from './slices/workpaperFilterSlice';
import { WorkpaperCompleteStatusEnum, WorkpaperSourceEnum, WorkpaperStatusEnum } from './types/workpaper';
import WorkpaperSidebar from './components/workpaper-sidebar/WorkpaperSidebar';
import { useSyncExternalTrialBalance } from './queries/use-trial-balance';
import { useFirmContext } from './providers/FirmProvider';
import { useWorkpaperAccounts } from './queries/use-workpaper-accounts';

function WorkpaperContainer(): JSX.Element {
    const { workpaperId } = useParams();
    const dispatch = useDispatch();
    const workpaperQuery = useWorkpaper(workpaperId);
    const entityQuery = useEntity(workpaperQuery.data?.workpaper.entity.id);

    const { mutate } = useSyncExternalTrialBalance(workpaperId);
    const { mutate: mutateEntity } = useRefreshXeroEntity(workpaperQuery.data?.workpaper.entity.id);
    const { mutate: mutateFYI } = useRefreshFYIDocuments(true);

    // Cleanup
    useEffect(() => {
        const workpaperName = workpaperQuery.data?.workpaper.name as string;
        const entityName = workpaperQuery.data?.workpaper.entity.name as string;
        const prevTitle = document.title;

        if (workpaperName) {
            document.title = `${workpaperName} | ${entityName} | Cimplico Workpapers`;
        }

        if (workpaperQuery.data?.workpaper?.provider && workpaperQuery.data?.workpaper?.status === WorkpaperStatusEnum.ACTIVE) {
            mutate(true);
        }

        return (): void => {
            if (workpaperQuery.data?.workpaper.id) {
                dispatch(del({ workpaperId: workpaperQuery.data?.workpaper.id }));
            }
            document.title = prevTitle;
        };
    }, [
        dispatch,
        mutate,
        workpaperQuery.data?.workpaper.provider,
        workpaperQuery.data?.workpaper.id,
        workpaperQuery.data?.workpaper.status,
        workpaperQuery.data?.workpaper.name,
        workpaperQuery.data?.workpaper.entity.name,
    ]);

    // Refresh Xero entity if it doesn't exist have a short code yet for a Xero workpaper
    useEffect(() => {
        if (
            entityQuery.data?.entity &&
            !entityQuery.data?.entity.integrations.xero?.shortCode &&
            workpaperQuery.data?.workpaper?.provider === WorkpaperSourceEnum.XERO
        ) {
            mutateEntity({ entityId: entityQuery.data?.entity.id, refreshShortCodeOnly: true });
        }
    }, [entityQuery.data?.entity, workpaperQuery.data?.workpaper?.provider, mutateEntity]);

    // Refresh FYI documents if workpaper has an FYI integration
    useEffect(() => {
        if (
            workpaperQuery.data?.workpaper?.status === WorkpaperStatusEnum.ACTIVE &&
            workpaperQuery.data.workpaper.integrations?.fyi?.job.id
        ) {
            mutateFYI({ workpaperId: workpaperQuery.data?.workpaper.id as string });
        }
    }, [
        workpaperQuery.data?.workpaper?.status,
        workpaperQuery.data?.workpaper?.id,
        workpaperQuery.data?.workpaper?.integrations?.fyi?.job.id,
        mutateFYI,
    ]);

    return (
        <WorkpaperProvider>
            <WorkpaperContent />
        </WorkpaperProvider>
    );
}

function WorkpaperContent(): JSX.Element {
    const { status, completeOutcome } = useWorkpaperContext();

    return (
        <div className="flex w-full h-[var(--remaining-screen-height)] bg-white dark:bg-gray-800">
            <WorkpaperSidebar />
            <motion.div className="overflow-y-auto xl:px-16 px-8 mt-4 w-full relative" layout style={{ scrollbarGutter: 'stable' }}>
                <div className="m-auto">
                    {/* <MissingAccountMapWarning /> */}
                    <TemplateWarning />
                    {status && status !== WorkpaperStatusEnum.ACTIVE && (
                        <AlertV2
                            title={`This workpaper is ${status.toLowerCase()} and is read-only mode.`}
                            type="success"
                            className="mb-2"
                        />
                    )}
                    {status === WorkpaperStatusEnum.ACTIVE && completeOutcome?.status === WorkpaperCompleteStatusEnum.COMPLETING && (
                        <AlertV2 title={`This workpaper is completing and is read-only mode.`} type="info" className="mb-2" />
                    )}
                    {status === WorkpaperStatusEnum.ACTIVE && completeOutcome?.status === WorkpaperCompleteStatusEnum.COMPLETE_ERROR && (
                        <AlertV2
                            title={`Cannot complete workpaper.`}
                            description={[completeOutcome?.completeError?.message ?? 'Please try again.']}
                            type="error"
                            className="mb-2"
                        />
                    )}
                </div>
                <Outlet />
            </motion.div>
        </div>
    );
}

function MissingAccountMapWarning(): JSX.Element {
    const [hide, setHide] = useState(false);

    const { id } = useWorkpaperContext();
    const { shortId } = useFirmContext();
    const navigate = useNavigate();
    const accountsQuery = useWorkpaperAccounts(id, false);

    if (accountsQuery.isLoading) {
        return <></>;
    }
    if (accountsQuery.data?.accounts?.some((a) => !a.entityAccountId) && !hide) {
        return (
            <AlertV2
                type="warn"
                className="mb-2"
                title="Unmapped accounts"
                description="There are accounts in this workpaper that don't have a matching entity account. This may cause syncing issues, please go to workpaper settings to configure the mapping."
                onClose={(): void => setHide(true)}
            >
                <AlertV2.Action label="Go to settings" onClick={(): void => navigate(`/${shortId}/workpapers/${id}/settings?tab=chart`)} />
            </AlertV2>
        );
    }
    return <></>;
}

function TemplateWarning(): JSX.Element | null {
    const [hide, setHide] = useState(false);
    const { shortId } = useFirmContext();
    const navigate = useNavigate();
    const { procedureTemplateId, checklistId, status, id } = useWorkpaperContext();

    if ((!procedureTemplateId || !checklistId) && status === WorkpaperStatusEnum.ACTIVE && !hide) {
        return (
            <AlertV2
                className="mb-2"
                title="No procedure or checklist templated configured."
                description={['The trial balance may fail to refresh correctly until a new template is selected in settings.']}
                type="warn"
                onClose={(): void => setHide(true)}
            >
                <AlertV2.Action label="Go to settings" onClick={(): void => navigate(`/${shortId}/workpapers/${id}/settings`)} />
                <AlertV2.ExternalLink
                    label="Learn more"
                    href="https://support.cimplico.com/en/articles/10305238-editing-workpaper-settings"
                />
            </AlertV2>
        );
    }
    return null;
}

export default WorkpaperContainer;
